import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

export const LineupManager = ({ matchId }) => {
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [lineup, setLineup] = useState([]);
  const [lineupStatus, setLineupStatus] = useState(null);
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Formation options
  const formations = [
    '4-4-2', '4-3-3', '4-2-3-1', '3-5-2',
    '3-4-3', '5-3-2', '4-5-1', '5-4-1'
  ];

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // First load match to get team IDs
      const matchRes = await axios.get(`${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/api/matches/${matchId}`, { headers });
      const matchData = matchRes.data.data;
      setMatch(matchData);

      // Then load data with team filters
      const [lineupRes, statusRes, playersRes, positionsRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/api/lineups/${matchId}`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/api/lineups/${matchId}/status`, { headers }),
        // Filter players by match teams only
        axios.get(`${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/api/players?teamIds=${matchData.home_team_id},${matchData.away_team_id}`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/api/positions`, { headers })
      ]);

      setLineup(lineupRes.data);
      setLineupStatus(statusRes.data);
      setAvailablePlayers(playersRes.data.data || []);
      setPositions(positionsRes.data.data || []);
      setError(null);
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load lineup data');
    } finally {
      setLoading(false);
    }
  };

  const getPlayerLineup = (playerId, teamId) => {
    return lineup.find(p => p.player_id === playerId && p.team_id === teamId);
  };

  const getTeamStatus = (teamId) => {
    if (!lineupStatus) return null;
    return teamId === match?.home_team_id ? lineupStatus.homeTeam : lineupStatus.awayTeam;
  };

  const handleCheckbox = async (player, teamId, type) => {
    const existing = getPlayerLineup(player.id, teamId);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      setError(null);
      
      if (type === 'none') {
        // Remove from lineup
        if (existing) {
          await axios.delete(`${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/api/lineups/${existing.id}`, { headers });
        }
      } else {
        // Add or update
        const isStarting = type === 'starting';
        
        if (existing) {
          await axios.put(`${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/api/lineups/${existing.id}`, {
            is_starting: isStarting,
            position_id: existing.position_id,
            jersey_number: existing.jersey_number
          }, { headers });
        } else {
          await axios.post(`${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/api/lineups/${matchId}`, {
            playerId: player.id,
            teamId,
            positionId: positions[0]?.id || 1,
            jerseyNumber: player.id,
            isStarting,
            isCaptain: false
          }, { headers });
        }
      }
      await loadData();
    } catch (error) {
      console.error('Error updating lineup:', error);
      // Display validation errors from backend
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError('Failed to update lineup');
      }
      // Reload to reset UI state
      await loadData();
    }
  };

  const handleFieldUpdate = async (lineupId, field, value) => {
    try {
      setError(null);
      const token = localStorage.getItem('token');
      await axios.put(`${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/api/lineups/${lineupId}`, {
        [field]: value
      }, { headers: { Authorization: `Bearer ${token}` }});
      
      await loadData();
    } catch (error) {
      console.error('Error updating field:', error);
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError('Failed to update field');
      }
      await loadData();
    }
  };

  const handleCaptainChange = async (lineupId, teamId) => {
    try {
      setError(null);
      const token = localStorage.getItem('token');
      
      // Set this player as captain (backend will clear previous captain)
      await axios.put(`${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/api/lineups/${lineupId}`, {
        isCaptain: true
      }, { headers: { Authorization: `Bearer ${token}` }});
      
      await loadData();
    } catch (error) {
      console.error('Error setting captain:', error);
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError('Failed to set captain');
      }
      await loadData();
    }
  };

  const handleFormationChange = async (teamId, formation) => {
    try {
      setError(null);
      const token = localStorage.getItem('token');
      const isHomeTeam = teamId === match.home_team_id;

      await axios.put(
        `${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/api/matches/${matchId}`,
        isHomeTeam ? { home_formation: formation } : { away_formation: formation },
        { headers: { Authorization: `Bearer ${token}` }}
      );

      await loadData();
    } catch (error) {
      console.error('Error updating formation:', error);
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError('Failed to update formation');
      }
      await loadData();
    }
  };

  const renderTeamColumn = (teamId, teamName) => {
    const teamStatus = getTeamStatus(teamId);
    const startingCount = teamStatus?.starting || 0;
    const subCount = teamStatus?.subs || 0;
    const canAddStarting = teamStatus?.canAddStarting ?? true;
    const canAddSub = teamStatus?.canAddSub ?? true;

    // Check if team has a captain
    const hasCaptain = lineup.some(p => 
      p.team_id === teamId && 
      p.is_starting && 
      p.is_captain
    );

    const isHomeTeam = teamId === match?.home_team_id;
    const currentFormation = isHomeTeam ? match?.home_formation : match?.away_formation;

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold mb-2 text-center">{teamName}</h2>

        {/* Formation Selector */}
        <div className="mb-4 flex justify-center">
          <div className="inline-flex items-center gap-2">
            <label className="text-sm font-semibold text-gray-700">Formation:</label>
            <select
              value={currentFormation || ''}
              onChange={(e) => handleFormationChange(teamId, e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select...</option>
              {formations.map(formation => (
                <option key={formation} value={formation}>{formation}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mb-4 text-sm flex justify-between items-center">
          <div>
            <span className={`mr-4 ${startingCount === 11 ? 'text-green-600 font-bold' : startingCount > 11 ? 'text-red-600 font-bold' : ''}`}>
              Starting: <strong>{startingCount}/11</strong>
            </span>
            <span className={`${subCount > 12 ? 'text-red-600 font-bold' : ''}`}>
              Subs: <strong>{subCount}/12</strong>
            </span>
          </div>
          <div className="flex items-center gap-2">
            {startingCount === 11 && (
              hasCaptain ? (
                <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded font-medium">
                  ✓ Captain assigned
                </span>
              ) : (
                <span className="text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded font-medium">
                  ⚠ No captain
                </span>
              )
            )}
            {!canAddStarting && (
              <span className="text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded">
                Starting lineup full
              </span>
            )}
          </div>
        </div>

        <table className="w-full text-sm">
          <thead className="border-b-2 bg-gray-50">
            <tr>
              <th className="text-left py-2 px-2">Player</th>
              <th className="text-center py-2 w-20">Starting</th>
              <th className="text-center py-2 w-20">Sub</th>
              <th className="text-center py-2 w-16">Captain</th>
              <th className="text-center py-2 w-24">Pos</th>
              <th className="text-center py-2 w-16">#</th>
            </tr>
          </thead>
          <tbody>
            {availablePlayers
              .filter(player => player.teams?.some(t => t.team_id === teamId))
              .map(player => {
              const playerLineup = getPlayerLineup(player.id, teamId);
              const isStarting = playerLineup?.is_starting;
              const isSub = playerLineup && !playerLineup.is_starting;
              const isCaptain = playerLineup?.is_captain;
              
              // Disable checkboxes based on limits
              const canCheckStarting = isStarting || canAddStarting;
              const canCheckSub = isSub || canAddSub;

              return (
                <tr key={player.id} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-2">
                    {player.first_name} {player.last_name}
                    {isCaptain && <span className="ml-2 text-yellow-600 font-bold">©</span>}
                  </td>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      checked={isStarting || false}
                      disabled={!canCheckStarting}
                      onChange={() => handleCheckbox(player, teamId, isStarting ? 'none' : 'starting')}
                      className={`w-4 h-4 ${!canCheckStarting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      title={!canCheckStarting ? 'Maximum 11 starting players reached' : ''}
                    />
                  </td>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      checked={isSub || false}
                      disabled={!canCheckSub}
                      onChange={() => handleCheckbox(player, teamId, isSub ? 'none' : 'sub')}
                      className={`w-4 h-4 ${!canCheckSub ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      title={!canCheckSub ? 'Maximum 12 substitute players reached' : ''}
                    />
                  </td>
                  <td className="text-center">
                    {playerLineup && isStarting ? (
                      <input
                        type="radio"
                        name={`captain-${teamId}`}
                        checked={isCaptain || false}
                        onChange={() => handleCaptainChange(playerLineup.id, teamId)}
                        className="w-4 h-4 cursor-pointer"
                        title="Set as captain"
                      />
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="text-center">
                    {playerLineup ? (
                      <select
                        value={playerLineup.position_id}
                        onChange={(e) => handleFieldUpdate(playerLineup.id, 'positionId', parseInt(e.target.value))}
                        className="border rounded px-1 py-1 text-xs w-full"
                      >
                        {positions.map(pos => (
                          <option key={pos.id} value={pos.id}>{pos.name}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="text-center">
                    {playerLineup ? (
                      <input
                        type="number"
                        value={playerLineup.jersey_number}
                        onChange={(e) => handleFieldUpdate(playerLineup.id, 'jerseyNumber', parseInt(e.target.value))}
                        className="border rounded px-1 py-1 w-12 text-center text-xs"
                        min="1"
                        max="99"
                      />
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) return <div className="p-6">Loading...</div>;
  if (!match) return <div className="p-6">Match not found</div>;

  // Captain validation logic
  const lineupsComplete = lineupStatus?.homeTeam?.starting === 11 && 
                          lineupStatus?.awayTeam?.starting === 11;
  
  const homeCaptain = lineup.find(p => 
    p.team_id === match.home_team_id && 
    p.is_starting && 
    p.is_captain
  );
  const awayCaptain = lineup.find(p => 
    p.team_id === match.away_team_id && 
    p.is_starting && 
    p.is_captain
  );
  
  const captainsAssigned = homeCaptain && awayCaptain;
  const showCaptainWarning = lineupsComplete && !captainsAssigned;

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header with back button and match info */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/matches')}
          className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Matches
        </button>

        <div className="bg-white rounded-lg shadow p-6">
          <h1 className="text-3xl font-bold mb-2">Lineup Manager</h1>
          <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
            <div>
              <strong className="text-gray-800">Match:</strong> {match.home_team?.name} vs {match.away_team?.name}
            </div>
            <div>
              <strong className="text-gray-800">Date:</strong> {formatDate(match.match_date)}
            </div>
            <div>
              <strong className="text-gray-800">Tournament:</strong> {match.tournament?.name || 'N/A'}
            </div>
            <div>
              <strong className="text-gray-800">Venue:</strong> {match.venue?.name || 'N/A'}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded max-w-2xl mx-auto">
          <strong>Error:</strong> {error}
        </div>
      )}

      {showCaptainWarning && (
        <div className="mb-4 bg-yellow-50 border-l-4 border-yellow-400 p-4 max-w-2xl mx-auto">
          <p className="font-bold text-yellow-800">⚠ Missing Captains</p>
          <p className="text-sm text-yellow-700">
            Both teams need a captain assigned before the match can start. 
            Select a starting player as captain using the radio button.
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-6">
        {renderTeamColumn(match.home_team_id, match.home_team?.name)}
        {renderTeamColumn(match.away_team_id, match.away_team?.name)}
      </div>
    </div>
  );
};
